import api from '@/global/api';
import { getTimeRange, DAY, SHARED_TIME_RANGES } from '@/processes/Settings';
import qs from 'query-string';
import _toLower from 'lodash/toLower';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import { getFloatOrDecimalNumber } from '@/shared/utils/formatters';
import { convertNegativeToZero } from '@/shared/utils/convertNegativeToZero';
import { getApiMiningType } from '@/shared/utils/apiMiningType';

export const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getHashrates: build.query({
      query: (params = {}) => {
        const {
          dateRange = DAY,
          account,
          miner,
          coin,
          worker = '',
          offset = 0
        } = params;
        const { window, limit } = getTimeRange(coin, dateRange);
        const queryParams = qs.stringify({
          coin,
          worker,
          window,
          limit,
          offset,
        }, { skipNull: true })

        const { urlPart } = getApiMiningType({ miner, account, coin });

        return {
          url: `/v1/${urlPart}/hashrates?${queryParams}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const items = _get(response, 'items', []);
        return _sortBy(items, 'timestamp').map(item => (
          {
            ...item,
            ...{
              hashrate: getFloatOrDecimalNumber(item.hashrate),
              meanHashrate: getFloatOrDecimalNumber(item.meanHashrate),
              reportedHashrate: getFloatOrDecimalNumber(item.reportedHashrate),
            }
          }
        ));
      }
    }),
    getMiner: build.query({
      query: (params = {}) => {
        const {
          account,
          miner,
          coin,
        } = params;
        const queryParams = qs.stringify({
          coin,
        }, { skipNull: true })

        const { urlPart } = getApiMiningType({ miner, account, coin });

        return {
          url: `/v1/${urlPart}/miner?${queryParams}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        return {
          ...response,
          ...{
            hashrate: convertNegativeToZero(response.hashrate),
            hashrate24h: convertNegativeToZero(response.hashrate24h),
          }
        }
      }
    }),
    getHashratesShared: build.query({
      query: (params = {}) => {
        const {
          dateRange = DAY,
          miner: minerAddress,
          coin,
          worker = '',
          offset = 0
        } = params;
        const { window, limit } = _get(SHARED_TIME_RANGES, dateRange);
        const queryParams = qs.stringify({
          minerAddress,
          coin,
          worker,
          window,
          limit,
          offset,
        }, { skipNull: true })
        return {
          url: `/v1/stats/hashrates?${queryParams}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const items = _get(response, 'items', []);
        return _sortBy(items, 'timestamp').map(item => (
          {
            ...item,
            ...{
              hashrate: getFloatOrDecimalNumber(item.hashrate) || 0,
              meanHashrate: getFloatOrDecimalNumber(item.meanHashrate) || 0,
              reportedHashrate: getFloatOrDecimalNumber(item.reportedHashrate) || 0,
            }
          }
        ));
      }
    }),
    getMinerShared: build.query({
      query: (params = {}) => {
        const {
          miner: minerAddress,
          coin,
        } = params;
        return {
          url: `/v1/stats/miner/${minerAddress}/${coin}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const sharesStats = _get(response, 'sharesStatusStats', {});;
        return {
          ...response,
          ...{
            hashrate: getFloatOrDecimalNumber(response.hashrate) || 0,
            hashrate24h: getFloatOrDecimalNumber(response.hashrate24h) || 0,
            onlineWorkerCount: getFloatOrDecimalNumber(response.onlineWorkerCount) || 0,
            offlineWorkerCount: getFloatOrDecimalNumber(response.offlineWorkerCount) || 0,
            invalidCount: getFloatOrDecimalNumber(sharesStats.invalidCount) || 0,
            invalidRate: getFloatOrDecimalNumber(sharesStats.invalidRate) || 0,
            lastShareDt: sharesStats.lastShareDt,
            staleCount: getFloatOrDecimalNumber(sharesStats.staleCount) || 0,
            staleRate: getFloatOrDecimalNumber(sharesStats.staleRate) || 0,
            validCount: getFloatOrDecimalNumber(sharesStats.validCount) || 0,
            validRate: getFloatOrDecimalNumber(sharesStats.validRate) || 0,
          }
        }
      }
    }),
  }),
});

export const {
  useGetHashratesQuery,
  useGetMinerQuery,
  useGetHashratesSharedQuery,
  useGetMinerSharedQuery,

} = extendedApi;

export const {
  getHashrates,
  getMiner,
  getHashratesShared,
  getMinerShared,
} = extendedApi.endpoints;

